import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import styles from './EventSalesPage.module.css';

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatYen = (value) => `¥${toNumber(value).toLocaleString('ja-JP')}`;

// payoutStatus: チケット売上の振込状況, donationStatus: 投げ銭の振込状況
// payoutStatus: チケット売上の振込状況, donationStatus: 投げ銭の振込状況, gross: チケット売上, donationCount: 投げ銭件数
const payoutLabel = (payoutStatus, donationStatus, gross, donationCount) => {
  const hasTicketSales = Number(gross) > 0;
  const hasDonation = Number(donationCount) > 0;

  // チケット売上なし・投げ銭のみ
  if (!hasTicketSales && hasDonation) {
    if (donationStatus === 'paid') return '振込済';
    if (donationStatus === 'partial') return '一部振込';
    if (donationStatus === 'scheduled') return '振込予定';
    return '未振込';
  }

  // チケット売上あり・投げ銭なし
  if (hasTicketSales && !hasDonation) {
    if (payoutStatus === 'paid') return '振込済';
    if (payoutStatus === 'partial') return '一部振込';
    if (payoutStatus === 'scheduled') return '振込予定';
    return '未振込';
  }

  // 両方あり
  if (hasTicketSales && hasDonation) {
    if (payoutStatus === 'paid' && (!donationStatus || donationStatus === 'paid')) return '振込済';
    if (payoutStatus === 'partial' || donationStatus === 'partial') return '一部振込';
    if (payoutStatus === 'scheduled' || donationStatus === 'scheduled') return '振込予定';
    return '未振込';
  }

  // どちらもなし
  return '未振込';
};

const feeBearerLabel = (value) => (value === 'buyer' ? '購入者負担' : '主催者負担');

const EventSalesPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/events/${id}/sales-summary`);
        setData(res.data || null);
      } catch (e) {
        if (e?.response?.status === 403) {
          setError('この売上ページを閲覧する権限がありません。');
        } else {
          setError('売上データの取得に失敗しました。');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) load();
  }, [id]);

  const rows = useMemo(() => {
    const groups = Array.isArray(data?.groups) ? data.groups : [];
    return groups;
  }, [data]);

  const handleDownloadCsv = async () => {
    if (!id) return;

    setCsvLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/events/${id}/sales-summary.csv`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_summary_event_${id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert('CSVダウンロードに失敗しました。');
    } finally {
      setCsvLoading(false);
    }
  };

  if (loading) {
    return <div className={styles.page}><div className={styles.container}>読み込み中...</div></div>;
  }

  if (error) {
    return <div className={styles.page}><div className={styles.container}>{error}</div></div>;
  }

  const eventName = data?.event?.name || 'イベント';
  const eventDate = String(data?.event?.date || '').slice(0, 10);
  const venue = data?.event?.venue || '-';
  const feeBearer = data?.event?.fee_bearer || 'organizer';
  // payoutLabelに売上・投げ銭件数も渡す
  const payout = payoutLabel(
    data?.payout?.display_status,
    data?.donation?.payout_status,
    data?.gross,
    data?.donation?.count
  );

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>売上ページ</h1>
            <p className={styles.eventName}>{eventName}</p>
            <p className={styles.meta}>{eventDate}</p>
            <p className={styles.meta}>{venue}</p>
            <p className={styles.meta}>手数料負担: {feeBearerLabel(feeBearer)}</p>
          </div>
          <div className={styles.headerActions}>
            <span className={styles.payoutBadge}>{payout}</span>
            <button type="button" className={styles.csvButton} onClick={handleDownloadCsv} disabled={csvLoading}>
              {csvLoading ? 'CSV生成中...' : 'CSVダウンロード'}
            </button>
            <Link className={styles.backLink} to="/dashboard">ダッシュボードへ</Link>
          </div>
        </header>

        <section className={styles.ticketSummary}>
          <h2 className={styles.ticketSummaryTitle}>🎫 チケット</h2>
          <div className={styles.cards}>
            <article className={styles.card}>
              <div className={styles.cardLabel}>総売上</div>
              <div className={styles.cardValue}>{formatYen(data?.gross)}</div>
              <div className={styles.cardSub}>販売枚数: {toNumber(data?.total_quantity)}枚</div>
            </article>
            <article className={styles.card}>
              <div className={styles.cardLabel}>決済手数料（Stripe実績）</div>
              <div className={styles.cardValue}>{formatYen(data?.stripe_fee_total)}</div>
            </article>
            <article className={styles.card}>
              <div className={styles.cardLabel}>プラットフォーム手数料（3%）</div>
              <div className={styles.cardValue}>{formatYen(data?.platform_fee_total)}</div>
            </article>
          </div>
        </section>

        {toNumber(data?.donation?.count) > 0 && (
          <section className={styles.donationSummary}>
            <h2 className={styles.donationSummaryTitle}>💝 投げ銭</h2>
            <div className={styles.donationCards}>
              <article className={styles.donationCard}>
                <div className={styles.cardLabel}>件数</div>
                <div className={styles.cardValue}>{toNumber(data?.donation?.count)}件</div>
              </article>
              <article className={styles.donationCard}>
                <div className={styles.cardLabel}>投げ銭総額</div>
                <div className={styles.cardValue}>{formatYen(data?.donation?.gross)}</div>
              </article>
              <article className={styles.donationCard}>
                <div className={styles.cardLabel}>手数料（15%）</div>
                <div className={styles.cardValue}>{formatYen(data?.donation?.fee_total)}</div>
              </article>
            </div>
          </section>
        )}

        <section className={styles.payoutRow}>
          <h2 className={styles.payoutRowTitle}>合計</h2>
          <article className={`${styles.card} ${styles.cardNet}`}>
            <div className={styles.cardLabel}>
              振込予定金額{toNumber(data?.donation?.count) > 0 ? '（投げ銭含む）' : ''}
            </div>
            <div className={styles.cardValue}>{formatYen(toNumber(data?.net_total) + toNumber(data?.donation?.net_total))}</div>
          </article>
        </section>

        <section className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>価格タイプ</th>
                <th>単価</th>
                <th>販売枚数</th>
                <th>売上小計</th>
                <th>決済手数料（Stripe実績）</th>
                <th>PF手数料</th>
                <th>返金</th>
                <th>チャージバック金額</th>
                <th>チャージバック手数料</th>
                <th>振込予定額</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((group) => (
                <React.Fragment key={group.ticket_category}>
                  <tr className={styles.groupRow}>
                    <td colSpan={10}>{group.ticket_category}</td>
                  </tr>
                  {group.items.map((item) => (
                    <tr key={`${group.ticket_category}-${item.price_type}-${item.unit_price}`} className={item.has_refund_impact ? styles.refundRow : ''}>
                      {(() => {
                        const displayNet = toNumber(item.net);
                        return (
                          <>
                            <td>{item.price_type}</td>
                            <td>{formatYen(item.unit_price)}</td>
                            <td>{toNumber(item.quantity)}</td>
                            <td>{formatYen(item.gross)}</td>
                            <td>{formatYen(item.stripe_fee)}</td>
                            <td>{formatYen(item.platform_fee)}</td>
                            <td>{formatYen(item.refund)}</td>
                            <td>{formatYen(item.chargeback_amount)}</td>
                            <td>{formatYen(item.chargeback_fee)}</td>
                            <td className={displayNet < 0 ? styles.negativeNet : styles.positiveNet}>{formatYen(displayNet)}</td>
                          </>
                        );
                      })()}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={10} className={styles.empty}>売上データがありません。</td>
                </tr>
              )}
              {toNumber(data?.donation?.count) > 0 && (
                <React.Fragment key="donation">
                  <tr className={styles.groupRow}>
                    <td colSpan={10}>💝 投げ銭</td>
                  </tr>
                  <tr>
                    <td>投げ銭</td>
                    <td>-</td>
                    <td>{toNumber(data?.donation?.count)}件</td>
                    <td>{formatYen(data?.donation?.gross)}</td>
                    <td>-</td>
                    <td>{formatYen(data?.donation?.fee_total)}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td className={styles.positiveNet}>{formatYen(data?.donation?.net_total)}</td>
                  </tr>
                </React.Fragment>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
};

export default EventSalesPage;
